"use client";

import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { HeartPulse, LayoutDashboard, MessageSquarePlus, Navigation, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next"; 

import useAssistantContext from "../../hooks/useAssistantContext";
import { sendAssistantMessage } from "../../redux/thunks/assistantThunks";
import { sendDoctorAssistantMessage } from "../../redux/thunks/doctorAssistantThunks.js";

import {
  assistantSessionStorageKey,
  createNewAgentSession,
  getPersistentAgentSessionId,
  getPersistentAssistantSessionId,
} from "./assistantSession.js";

import QuickActions from "../../guided/components/QuickActions/QuickActions.jsx";
import GuidedFlowRenderer from "../../guided/components/GuidedFlow/GuidedFlowRenderer.jsx";
import OnboardingModal from "../../guided/components/Shared/OnboardingModal.jsx";
import AgentIA from "../../pages/AgentIA/AgentIA.jsx";
import AssistantIA from "../../pages/AssistantIA/AssistantIA.jsx";
import { UI_GUIDED_FLOW, UI_QUICK_ACTIONS } from "../../guided/constants/guidedUiTypes.js";
import {
  PATIENT_BOOK_APPOINTMENT,
  PATIENT_CANCEL_APPOINTMENT,
  PATIENT_RESCHEDULE_APPOINTMENT,
} from "../../guided/constants/guidedActionIds.js";
import {
  cancelGuidedFlow,
  continueGuidedFlow,
  getGuidedCapabilities,
  startGuidedFlow,
} from "../../guided/services/guidedAssistantApi.js";
import AuthRequiredModal from "../../../Landing/components/AuthRequiredModal/AuthRequiredModal.jsx";

import Robot from "../../../assets/icons/robotchat.svg";
import RobotGauche from "../../../assets/images/gauche.svg";
import RobotDroite from "../../../assets/images/droite.svg";

import "./AssistantBubble.css";

/* ===================== ROBOT IMAGE ICON ===================== */
const RobotImageIcon = ({ size = 24, className = "", style = {} }) => (
  <img
    src={Robot}
    alt=""
    aria-hidden="true"
    className={className}
    style={{
      width: size,
      height: size,
      objectFit: "contain",
      display: "block",
      ...style,
    }}
  />
);

/* ===================== SCREEN CONSTANTS ===================== */
const SCREEN_LANDING = "landing";
const SCREEN_CHAT = "chat";
const SCREEN_AGENT = "agent";
const SCREEN_GESTION = "gestion";
const ASSISTANT_LANGUAGE_STORAGE_KEY = "assistant-selected-language";
const LandingSectionLogo = ({ type }) => {
  const icons = {
    gestion: LayoutDashboard,
    assistant: HeartPulse,
    agent: Navigation,
  };
  const Icon = icons[type] || Sparkles;

  return (
    <span className={`asst-category-logo asst-category-logo--${type}`} aria-hidden="true">
      <Icon size={26} strokeWidth={2.2} />
      <span className="asst-category-logo-glint">
        <Sparkles size={13} strokeWidth={2.4} />
      </span>
    </span>
  );
};

const normalizeAssistantLanguage = (value) => {
  const lang = String(value || "").toLowerCase();
  if (lang.startsWith("ar")) return "ar";
  return lang.startsWith("en") ? "en" : "fr";
};

const getInitialAssistantLanguage = (preferredLanguage = "") => {
  try {
    return normalizeAssistantLanguage(
      preferredLanguage ||
      localStorage.getItem("medilink-lang") ||
      document.documentElement.getAttribute("lang") ||
      localStorage.getItem(ASSISTANT_LANGUAGE_STORAGE_KEY) ||
      navigator.language ||
      "fr"
    );
  } catch {
    return normalizeAssistantLanguage(preferredLanguage || "fr");
  }
};

const normalizeAssistantLabel = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const localizeAssistantLinkText = (text, lang = "fr", fallback = "", labels = {}) => {
  const normalized = normalizeAssistantLabel(text || fallback);
  return labels?.[normalized] || fallback || text || "";
};

const readAssistantBubbleObject = (t, key, lng) => {
  const value = t(`assistantBubble.${key}`, {
    ns: "assistantAi",
    lng,
    returnObjects: true,
  });
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
};

const readAssistantBubbleText = (t, key, lng) =>
  t(`assistantBubble.${key}`, { ns: "assistantAi", lng });

const parseStructuredMessage = (text = "") => {
  const lines = String(text || "").split(/\r?\n/).filter(Boolean);
  const itemLines = [];
  const suggestionLines = [];
  const narrative = [];
  const lowerText = String(text || "").toLowerCase();
  const normalizedText = lowerText.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const isAvailabilityContext =
    lowerText.includes("créneaux disponibles") ||
    lowerText.includes("creneaux disponibles") ||
    lowerText.includes("disponibilités") ||
    lowerText.includes("disponibilites") ||
    normalizedText.includes("availability") ||
    normalizedText.includes("available slots") ||
    normalizedText.includes("slots for");
  const isEventContext =
    normalizedText.includes("evenement") ||
    normalizedText.includes("evenements") ||
    normalizedText.includes("agenda d'evenements") ||
    normalizedText.includes("agenda d’evenements");
  const isEventSuggestionContext =
    lowerText.includes("plages disponibles ce jour") ||
    lowerText.includes("plages disponibles");

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (/^-\s*#\d+\s*\|/.test(trimmed)) {
      itemLines.push(trimmed);
    } else if (
      isEventSuggestionContext &&
      /^-\s*\d{2}\/\d{2}\/\d{4}\s+\d{1,2}:\d{2}\s*(?:→|->|â†’)\s*\d{1,2}:\d{2}/.test(trimmed)
    ) {
      suggestionLines.push(trimmed);
    } else {
      narrative.push(line);
    }
  });

  const items = itemLines.map((line) => {
    const parts = line.replace(/^-\s*/, "").split("|").map((part) => part.trim());
    const id = parts[0]?.replace("#", "") || "";
    const hasExplicitTitle = parts.length >= 4;
    const hasEventTitle = isEventContext && parts.length >= 3 && !hasExplicitTitle;
    const status = parts[parts.length - 1] || "";
    const datePart = hasExplicitTitle ? parts[2] : hasEventTitle ? parts[2] : parts[1];
    const kind =
      isAvailabilityContext || /disponible|indisponible/i.test(status)
        ? "availability"
        : hasEventTitle
          ? "event"
          : "appointment";
    const title = hasExplicitTitle
      ? parts[1]
      : kind === "event"
        ? parts[1]
        : kind === "availability"
        ? ""
        : "Créneau";

    return {
      id,
      title,
      datePart,
      status: hasEventTitle ? "" : status,
      raw: line,
      kind,
    };
  });

  const suggestions = suggestionLines.map((line, index) => {
    const datePart = line.replace(/^-\s*/, "").trim();
    return {
      id: `suggestion-${index}`,
      title: "Plage proposée",
      datePart,
      status: "Disponible",
      raw: line,
      kind: "eventSuggestion",
    };
  });

  return { narrative: narrative.join("\n"), items: [...items, ...suggestions] };
};

const statusClassName = (status = "") => {
  const normalized = status.toLowerCase();
  if (normalized.includes("cancel") || normalized.includes("indisponible") || normalized.includes("unavailable")) return "is-muted";
  if (normalized.includes("confirmed") || normalized.includes("disponible") || normalized.includes("available")) return "is-ok";
  if (normalized.includes("scheduled") || normalized.includes("pending") || normalized.includes("attente")) return "is-waiting";
  return "is-neutral";
};

const translateAssistantStatus = (status = "", lang = "fr") => {
  const normalized = String(status || "").toLowerCase();
  const isEnglish = lang === "en";
  if (normalized.includes("confirmed")) return isEnglish ? "Confirmed" : "Confirmé";
  if (normalized.includes("scheduled")) return isEnglish ? "Scheduled" : "Planifié";
  if (normalized.includes("pending")) return isEnglish ? "Pending" : "En attente";
  if (normalized.includes("cancel")) return isEnglish ? "Cancelled" : "Annulé";
  if (normalized.includes("rescheduled")) return isEnglish ? "Rescheduled" : "Replanifié";
  if (normalized.includes("unavailable")) return isEnglish ? "Unavailable" : "Indisponible";
  if (normalized.includes("available")) return isEnglish ? "Available" : "Disponible";
  if (normalized.includes("indisponible")) return isEnglish ? "Unavailable" : "Indisponible";
  if (normalized.includes("disponible")) return isEnglish ? "Available" : "Disponible";
  return status;
};

const extractStructuredItemDate = (datePart = "") => {
  const value = String(datePart || "");
  const iso = value.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso) return iso[0];
  const fr = value.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/);
  if (fr) {
    const [, day, month, year] = fr;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  return value.trim();
};

/* ===================== MAIN COMPONENT ===================== */
export default function AssistantBubble({ CustomIcon, mode = "patient" }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { t, i18n } = useTranslation(); // ✅ i18n hook

  const resolvedMode = useMemo(() => {
    const m = String(mode || "patient").toLowerCase();
    return m === "doctor" ? "doctor" : "patient";
  }, [mode]);

  const ctx = useAssistantContext(resolvedMode);

  const [open, setOpen] = useState(false);
  const [isMax, setIsMax] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [expandedMessageIndexes, setExpandedMessageIndexes] = useState(() => new Set());
  const [screen, setScreen] = useState(SCREEN_LANDING);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [assistantLanguage, setAssistantLanguage] = useState(() =>
    getInitialAssistantLanguage(i18n?.resolvedLanguage || i18n?.language)
  );
  const [quickActions, setQuickActions] = useState([]);
  const [quickActionsLoading, setQuickActionsLoading] = useState(false);
  const [quickActionsError, setQuickActionsError] = useState(null);
  const [guidedBusy, setGuidedBusy] = useState(false);
  const [onboardingSection, setOnboardingSection] = useState(null);
  const [pendingSection, setPendingSection] = useState(null);
  const [chatMode, setChatMode] = useState("assistant");

  const [fabPosition, setFabPosition] = useState(() => {
    const saved = localStorage.getItem("asst-fab-position");
    if (saved) { try { return JSON.parse(saved); } catch { return { right: 24, bottom: 24 }; } }
    return { right: 24, bottom: 24 };
  });

  const [isDraggingFab, setIsDraggingFab] = useState(false);
  const [fabDragOffset, setFabDragOffset] = useState({ x: 0, y: 0 });
  const [fabHasMoved, setFabHasMoved] = useState(false);

  const [panelPosition, setPanelPosition] = useState(() => {
    const saved = localStorage.getItem("asst-panel-position");
    if (saved) { try { return JSON.parse(saved); } catch { return { right: 24, bottom: 10 }; } }
    return { right: 24, bottom: 10 };
  });

  const fetchUserLocation = () => {
    if (!navigator.geolocation) { console.warn("[Assistant] Geolocation not supported"); return; }
    if (!userCoords) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = { lat: position.coords.latitude, lng: position.coords.longitude };
          setUserCoords(coords);
        },
        (error) => console.warn("[Assistant] Location denied or error:", error.message),
        { timeout: 10000, maximumAge: 60000 }
      );
    }
  };

  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  const [panelDragOffset, setPanelDragOffset] = useState({ x: 0, y: 0 });

  const [sessionId, setSessionId] = useState(() =>
    getPersistentAssistantSessionId(resolvedMode, ctx?.userId)
  );
  const [agentSessionId, setAgentSessionId] = useState(() =>
    getPersistentAgentSessionId(resolvedMode, ctx?.userId)
  );
  const sessionIdRef = useRef(sessionId);
  const sessionOwnerRef = useRef(assistantSessionStorageKey(resolvedMode, ctx?.userId));
  const inFlightChatRef = useRef(null);
  const [chatBusy, setChatBusy] = useState(false);

  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const fabRef = useRef(null);
  const panelRef = useRef(null);

  const RobotIcon = CustomIcon || RobotImageIcon;
  const timeLocale = assistantLanguage === "ar" ? "ar-TN" : assistantLanguage === "en" ? "en-US" : "fr-FR";
  const assistantPanelCopy = useMemo(
    () => readAssistantBubbleObject(t, "panelCopy", assistantLanguage),
    [t, assistantLanguage]
  );
  const assistantCopy = useMemo(
    () => readAssistantBubbleObject(t, "chatCopy", assistantLanguage),
    [t, assistantLanguage]
  );
  const assistantLinkLabels = useMemo(
    () => readAssistantBubbleObject(t, "linkLabels", assistantLanguage),
    [t, assistantLanguage]
  );
  const welcomeMessage = useMemo(
    () => readAssistantBubbleText(t, `welcome.${resolvedMode === "doctor" ? "doctor" : "patient"}`, assistantLanguage),
    [t, resolvedMode, assistantLanguage]
  );
  const landingSections = useMemo(() => {
    const sections = readAssistantBubbleObject(t, "landingSections", assistantLanguage);
    return ["gestion", "assistant", "agent"].map((id) => ({
      id,
      icon: id === "assistant" ? "AI" : id === "gestion" ? "G" : "A",
      title: sections?.[id]?.title || "",
      subtitle: sections?.[id]?.subtitle || "",
      description: sections?.[id]?.description || "",
    }));
  }, [t, assistantLanguage]);
  const isRtl = assistantLanguage === "ar";
  const userMessageCount = useMemo(
    () => messages.filter((m) => m.role === "user" || m.sender === "user").length,
    [messages]
  );

  const handleAssistantLanguageChange = (language) => {
    const nextLanguage = normalizeAssistantLanguage(language);
    setAssistantLanguage(nextLanguage);
    if (normalizeAssistantLanguage(i18n?.resolvedLanguage || i18n?.language) !== nextLanguage) {
      i18n?.changeLanguage?.(nextLanguage);
    }
  };

  useEffect(() => {
    const siteLanguage = normalizeAssistantLanguage(i18n?.resolvedLanguage || i18n?.language || "fr");
    setAssistantLanguage((current) => (current === siteLanguage ? current : siteLanguage));
  }, [i18n?.resolvedLanguage, i18n?.language]);

  useEffect(() => {
    return () => undefined;
  }, [resolvedMode]);

  useLayoutEffect(() => {
    const owner = assistantSessionStorageKey(resolvedMode, ctx?.userId);
    if (sessionOwnerRef.current === owner) return;
    inFlightChatRef.current?.abort?.();
    inFlightChatRef.current = null;
    sessionOwnerRef.current = owner;
    const next = getPersistentAssistantSessionId(resolvedMode, ctx?.userId);
    sessionIdRef.current = next;
    setSessionId(next);
    setAgentSessionId(getPersistentAgentSessionId(resolvedMode, ctx?.userId));
    setChatBusy(false);
    setMessages([]);
    setInput("");
  }, [resolvedMode, ctx?.userId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!ctx?.authenticated) return;
    const key = `assistant-welcomed-${resolvedMode}`;
    if (!sessionStorage.getItem(key)) sessionStorage.setItem(key, "1");
  }, [ctx?.authenticated, resolvedMode]);

  useEffect(() => {
    if (open && screen === SCREEN_CHAT && inputRef.current) inputRef.current.focus();
  }, [open, screen]);

  useEffect(() => {
    if (!open || ![SCREEN_CHAT, SCREEN_GESTION].includes(screen) || !resolvedMode) return;

    let active = true;
    setQuickActionsLoading(true);
    setQuickActionsError(null);

    getGuidedCapabilities(resolvedMode)
      .then((data) => {
        if (active) setQuickActions(Array.isArray(data?.groups) ? data.groups : []);
      })
      .catch((err) => {
        console.warn("[AssistantBubble] Guided capabilities unavailable:", err);
        if (active) {
          setQuickActions([]);
          setQuickActionsError("Les actions rapides sont momentanement indisponibles.");
        }
      })
      .finally(() => {
        if (active) setQuickActionsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, screen, resolvedMode]);

  const contextualQuickActions = useMemo(() => {
    const route = String(location?.pathname || "").toLowerCase();
    if (!quickActions.length) return [];

    const priorityByRoute = [
      { match: ["appointment", "rendez"], groups: ["appointments"] },
      { match: ["medicalrecord", "records", "profile"], groups: ["medical_record", "patients"] },
      { match: ["medicine", "medication"], groups: ["medications"] },
      { match: ["pharmac", "lab", "clinique", "doctor-finder"], groups: ["locations"] },
      { match: ["feeddoctor"], groups: ["appointments", "availability", "patients", "events"] },
      { match: ["feed"], groups: ["appointments", "medical_record", "medications", "locations"] },
    ];
    const matched = priorityByRoute.find((item) => item.match.some((token) => route.includes(token)));
    if (!matched) return quickActions;

    const score = (group) => {
      const index = matched.groups.indexOf(group.id);
      return index === -1 ? 99 : index;
    };
    return [...quickActions].sort((a, b) => score(a) - score(b));
  }, [location?.pathname, quickActions]);

  const gestionQuickActions = useMemo(
    () => contextualQuickActions.filter((group) => !["health", "clinical"].includes(group?.id)),
    [contextualQuickActions]
  );
  const gestionHasActiveFlow = messages.some((message) =>
    message?.sender === "bot" && (message?.ui?.type !== UI_QUICK_ACTIONS) && (message?.ui || message?.text)
  ) || guidedBusy;

  useEffect(() => {
    localStorage.setItem(ASSISTANT_LANGUAGE_STORAGE_KEY, normalizeAssistantLanguage(assistantLanguage));
  }, [assistantLanguage]);

  useEffect(() => {
    setMessages((prev) =>
      prev.map((message) =>
        message?.meta?.welcome === true
          ? { ...message, text: welcomeMessage }
          : message
      )
    );
  }, [welcomeMessage]);

  useEffect(() => {
    localStorage.setItem("asst-fab-position", JSON.stringify(fabPosition));
  }, [fabPosition]);

  useEffect(() => {
    localStorage.setItem("asst-panel-position", JSON.stringify(panelPosition));
  }, [panelPosition]);

  const busy = chatBusy || guidedBusy;

  const buildContext = (activeSessionId = sessionIdRef.current) => ({
    session_id: activeSessionId,
    currentRoute: location?.pathname || "/",
    location: userCoords,
    user: {
      id: ctx?.userId ?? null,
      role: ctx?.role ?? resolvedMode,
      authenticated: !!ctx?.authenticated,
    },
    ...ctx,
    locale: assistantLanguage,
    response_locale: assistantLanguage,
    assistant_language: assistantLanguage,
    selected_language: assistantLanguage,
    locale_source: "user_selection",
    language_source: "user_selection",
  });

  async function handleSend(text, displayText = null) {
    const trimmed = (text || input).trim();
    if (!trimmed || inFlightChatRef.current) return;
    const requestSessionId = sessionIdRef.current;
    const visibleText = (displayText || trimmed).trim();

    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        text: visibleText,
        links: [],
        action: null,
        timestamp: new Date().toLocaleTimeString(timeLocale, {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);

    setInput("");
    setChatBusy(true);

    const thunk = resolvedMode === "doctor" ? sendDoctorAssistantMessage : sendAssistantMessage;

    const request = dispatch(thunk({ text: trimmed, context: buildContext(requestSessionId) }));
    inFlightChatRef.current = request;
    request
      .unwrap()
      .then((reply) => {
        if (sessionIdRef.current !== requestSessionId) return;
        setMessages((prev) => [...prev, {
          sender: "bot",
          text: reply?.message || t("medaz.chat.defaultBotError"),
          links: reply?.links || [],
          action: reply?.action || null,
          data: reply?.data || {},
          ui: reply?.ui || null,
          meta: reply?.meta,
          timestamp: new Date().toLocaleTimeString(timeLocale, { hour: "2-digit", minute: "2-digit" }),
        }]);
      })
      .catch((err) => {
        if (sessionIdRef.current !== requestSessionId) return;
        console.error("[AssistantBubble] Send error:", err);
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: t("medaz.chat.defaultBotError"),
            links: [],
            action: null,
            timestamp: new Date().toLocaleTimeString(timeLocale, {
              hour: "2-digit",
              minute: "2-digit",
            }),
          },
        ]);
      })
      .finally(() => {
        if (inFlightChatRef.current === request) {
          inFlightChatRef.current = null;
          setChatBusy(false);
        }
      });
  }

  const appendGuidedResponse = (reply) => {
    const nextMessage = {
        sender: "bot",
        text: reply?.message || "Parcours guide initialise.",
        links: [],
        action: null,
        data: {},
        ui: reply?.ui || null,
        meta: reply?.meta || null,
        timestamp: new Date().toLocaleTimeString(timeLocale, {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
    const flowId = reply?.flowId || reply?.ui?.props?.flowId;
    const shouldReplaceGptCard = reply?.ui?.type === "gpt_response_card" && flowId;

    setMessages((prev) => {
      if (!shouldReplaceGptCard) return [...prev, nextMessage];
      const index = [...prev]
        .reverse()
        .findIndex((message) => message?.ui?.type === "gpt_response_card" && message?.ui?.props?.flowId === flowId);
      if (index === -1) return [...prev, nextMessage];
      const realIndex = prev.length - 1 - index;
      return prev.map((message, messageIndex) => (messageIndex === realIndex ? nextMessage : message));
    });
  };

  const buildWelcomeMessage = () => ({
    sender: "bot",
    text: welcomeMessage,
    links: [],
    meta: { welcome: true },
    timestamp: new Date().toLocaleTimeString(timeLocale, {
      hour: "2-digit",
      minute: "2-digit",
    }),
  });

  const handleNewConversation = () => {
    setAgentSessionId(createNewAgentSession(resolvedMode, ctx?.userId));
  };

  const buildGuidedBotMessage = (reply, fallback = "Parcours guide initialise.") => ({
    sender: "bot",
    text: reply?.message || fallback,
    links: [],
    action: null,
    data: {},
    ui: reply?.ui || null,
    meta: reply?.meta || null,
    timestamp: new Date().toLocaleTimeString(timeLocale, {
      hour: "2-digit",
      minute: "2-digit",
    }),
  });

  const handleQuickActionClick = async (actionId, action) => {
    if (!actionId || guidedBusy) return;

    const directRoute = action?.routeUrl || action?.url || action?.href;
    const launchedFromGestion = screen === SCREEN_GESTION;
    if (!directRoute) {
      setChatMode("gestion");
      if (launchedFromGestion) {
        setMessages([]);
      } else {
        setScreen(SCREEN_CHAT);
      }
    }
    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        text: action?.label || actionId,
        links: [],
        action: null,
        timestamp: new Date().toLocaleTimeString(timeLocale, {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);

    if (directRoute) {
      setOpen(false);
      setTimeout(() => {
        window.location.href = directRoute;
      }, 250);
      return;
    }

    setGuidedBusy(true);
    try {
      const reply = await startGuidedFlow({
        sessionId,
        role: resolvedMode,
        actionId,
        locale: assistantLanguage,
        context: buildContext(),
      });
      appendGuidedResponse(reply);
    } catch (err) {
      console.error("[AssistantBubble] Guided start error:", err);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "Je n'ai pas pu demarrer ce parcours guide pour le moment.",
          links: [],
          action: null,
          timestamp: new Date().toLocaleTimeString(timeLocale, {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
    } finally {
      setGuidedBusy(false);
    }
  };

  const handleGuidedContinue = async (props, payload = {}) => {
    if (!props?.flowId || guidedBusy) return;
    setGuidedBusy(true);
    try {
      // Multi-slot payloads stay grouped so the backend can ask for one confirmation before execution.
      if (false && Array.isArray(payload.slotIds) && payload.slotIds.length > 1) {
        const replies = [];
        const { slotIds, ...singlePayload } = payload;

        for (const slotId of slotIds) {
          const reply = await continueGuidedFlow({
            sessionId,
            role: resolvedMode,
            flowId: props.flowId,
            stepId: props.stepId,
            locale: assistantLanguage,
            payload: { ...singlePayload, slotId },
          });
          replies.push(reply);
        }

        const successfulCount = replies.filter((reply) => reply?.ui?.props?.ok !== false).length;
        const lastReply = replies[replies.length - 1];

        appendGuidedResponse({
          message: `${successfulCount}/${slotIds.length} créneaux traités.`,
          ui: {
            type: "confirmation_card",
            props: {
              title: successfulCount === slotIds.length ? "Créneaux bloqués" : "Blocage partiel",
              description: `${successfulCount} créneau${successfulCount > 1 ? "x" : ""} bloqué${successfulCount > 1 ? "s" : ""} sur ${slotIds.length}.`,
              ok: successfulCount === slotIds.length,
              primaryAction: lastReply?.ui?.props?.primaryAction,
            },
          },
          meta: lastReply?.meta || null,
        });
        return;
      }

      const reply = await continueGuidedFlow({
        sessionId,
        role: resolvedMode,
        flowId: props.flowId,
        stepId: props.stepId,
        locale: assistantLanguage,
        payload,
      });
      appendGuidedResponse(reply);
    } catch (err) {
      console.error("[AssistantBubble] Guided continue error:", err);
      appendGuidedResponse({ message: "Je n'ai pas pu continuer ce parcours guide pour le moment." });
    } finally {
      setGuidedBusy(false);
    }
  };

  const handleGuidedCancel = async () => {
    if (guidedBusy) return;
    setGuidedBusy(true);
    try {
      const reply = await cancelGuidedFlow({
        sessionId,
        role: resolvedMode,
      });
      appendGuidedResponse(reply);
    } catch (err) {
      console.error("[AssistantBubble] Guided cancel error:", err);
      appendGuidedResponse({ message: "Je n'ai pas pu annuler ce parcours guide pour le moment." });
    } finally {
      setGuidedBusy(false);
    }
  };

  const handleGestionHomeReturn = async () => {
    setMessages([]);
    setInput("");
    setChatMode("gestion");
    setScreen(SCREEN_GESTION);

    try {
      await cancelGuidedFlow({
        sessionId,
        role: resolvedMode,
      });
    } catch (err) {
      console.error("[AssistantBubble] Guided return menu error:", err);
    }
  };

  const appendQuickActionsMenu = async () => {
    if (guidedBusy) return;
    setGuidedBusy(true);
    try {
      let groups = quickActions;
      if (!groups.length) {
        const data = await getGuidedCapabilities(resolvedMode);
        groups = Array.isArray(data?.groups) ? data.groups : [];
        setQuickActions(groups);
      }
      const route = String(location?.pathname || "").toLowerCase();
      const routePriority = route.includes("feeddoctor")
        ? ["appointments", "availability", "patients", "events"]
        : route.includes("medicalrecord") || route.includes("records")
          ? ["medical_record", "medications", "appointments", "locations"]
          : route.includes("pharmac") || route.includes("lab")
            ? ["locations", "appointments", "medical_record", "medications"]
            : [];
      if (routePriority.length) {
        groups = [...groups].sort((a, b) => {
          const ai = routePriority.indexOf(a.id);
          const bi = routePriority.indexOf(b.id);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
      }
      groups = groups.filter((group) => !["health", "clinical"].includes(group?.id));

      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: assistantPanelCopy.quickActionPrompt,
          links: [],
          action: null,
          data: {},
          ui: {
            type: UI_QUICK_ACTIONS,
            props: { groups },
          },
          timestamp: new Date().toLocaleTimeString(timeLocale, {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
    } catch (err) {
      console.error("[AssistantBubble] Guided menu error:", err);
    } finally {
      setGuidedBusy(false);
    }
  };

  const renderQuickActions = (groups, key = "quick-actions", className = "") => (
    <QuickActions
      key={key}
      groups={groups}
      loading={quickActionsLoading}
      error={quickActionsError}
      onActionClick={handleQuickActionClick}
      role={resolvedMode}
      locale={assistantLanguage}
      className={className}
    />
  );

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  }

  /* ─── link helpers ─── */
  const hasUserIdInPath = (url) => {
    if (!url) return false;
    const uuidPattern = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidPattern.test(url.split("?")[0]);
  };

  const hasTourParams = (url) => url && url.includes("guide=true");

  const addTourParams = (url) => {
    if (!url) return "";
    try {
      const u = new URL(url, window.location.origin);
      if (!u.searchParams.has("guide")) u.searchParams.set("guide", "true");
      return u.pathname + u.search;
    } catch { return url; }
  };

  const getCleanUrl = (url) => {
    if (!url) return "";
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.delete("guide");
      u.searchParams.delete("tour");
      return u.pathname + (u.search || "");
    } catch { return url; }
  };

  const isAssistantIaInternalRoute = (url) => {
    if (!url) return false;
    try {
      const u = new URL(url, window.location.origin);
      const path = u.pathname.toLowerCase();
      const assistantTarget = String(
        u.searchParams.get("assistant") ||
        u.searchParams.get("mode") ||
        u.searchParams.get("assistantMode") ||
        ""
      ).toLowerCase();
      return ["/assistantia", "/assistant-ia"].includes(path) || assistantTarget === "health";
    } catch {
      const normalized = String(url || "").trim().toLowerCase();
      return (
        normalized.startsWith("/assistantia") ||
        normalized.startsWith("/assistant-ia") ||
        normalized.includes("assistant=health") ||
        normalized.includes("mode=health")
      );
    }
  };

  const handleLinkClick = (url, withTour = false) => {
    if (!url) return;
    if (isAssistantIaInternalRoute(url)) {
      openAssistantSection();
      return;
    }

    let finalUrl = url;
    if (withTour) {
      if (!hasTourParams(url)) finalUrl = addTourParams(url);
    } else {
      if (hasTourParams(url)) finalUrl = getCleanUrl(url);
    }
    setOpen(false);
    setTimeout(() => { window.location.href = finalUrl; }, 300);
  };

  const normalizeMedicalRedirectText = (value = "") =>
    String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const isMedicalAssistantRedirectMessage = (message) => {
    if (
      message?.meta?.medical_assistant_redirect === true ||
      message?.data?.assistant_internal_action === "open_patient_health_assistant"
    ) {
      return true;
    }

    const normalizedText = normalizeMedicalRedirectText(message?.text);
    const rawText = String(message?.text || "");
    return (
      (
        normalizedText.includes("cette question concerne la sante") &&
        normalizedText.includes("assistant sante")
      ) ||
      (
        normalizedText.includes("this question is about health") &&
        normalizedText.includes("health assistant")
      ) ||
      (
        rawText.includes("هذا السؤال") &&
        rawText.includes("مساعد الصحة")
      )
    );
  };

  const getMedicalAssistantRedirectLabel = (message) => {
    const fallback =
      assistantLanguage === "en"
        ? "Health assistant"
        : assistantLanguage === "ar"
          ? "مساعد الصحة"
          : "Assistant santé";
    return (
      message?.meta?.medical_assistant_button_label ||
      message?.data?.assistant_button_label ||
      fallback
    );
  };

  const handleExternalLinkClick = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleQuickReply = (command, displayText = null) => {
    if (!command) return;
    handleSend(command, displayText);
  };

  const handleAppointmentManagementClick = async (appointmentId, actionId, displayText) => {
    if (!appointmentId || guidedBusy || resolvedMode !== "patient") {
      const command = actionId === PATIENT_RESCHEDULE_APPOINTMENT
        ? `modifier rendez-vous #${appointmentId || ""}`.trim()
        : `annuler rendez-vous #${appointmentId || ""}`.trim();
      handleQuickReply(command, displayText);
      return;
    }

    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        text: displayText || String(appointmentId),
        links: [],
        action: null,
        timestamp: new Date().toLocaleTimeString(timeLocale, {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);

    setGuidedBusy(true);
    try {
      await startGuidedFlow({
        sessionId,
        role: "patient",
        actionId,
        locale: assistantLanguage,
        context: buildContext(),
      });
      const reply = await continueGuidedFlow({
        sessionId,
        role: "patient",
        flowId: actionId,
        stepId: "choose_appointment",
        locale: assistantLanguage,
        payload: { appointmentId: String(appointmentId) },
      });
      appendGuidedResponse(reply);
    } catch (err) {
      console.error("[AssistantBubble] Guided appointment management error:", err);
      handleQuickReply(
        actionId === PATIENT_RESCHEDULE_APPOINTMENT
          ? `modifier rendez-vous #${appointmentId}`.trim()
          : `annuler rendez-vous #${appointmentId}`.trim(),
        displayText,
      );
    } finally {
      setGuidedBusy(false);
    }
  };

  const handleDoctorInvite = (name, actionLabel) => {
    const visibleText = actionLabel || (assistantLanguage === "en" ? "Invite to MediLink" : "Inviter à MediLink");
    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        text: visibleText,
        links: [],
        action: null,
        timestamp: new Date().toLocaleTimeString(timeLocale, {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
      {
        sender: "bot",
        text: `${assistantCopy.inviteSent}${name ? ` ${assistantLanguage === "en" ? "Doctor" : "Médecin"} : ${name}` : ""}`,
        links: [],
        action: null,
        timestamp: new Date().toLocaleTimeString(timeLocale, {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
  };

  const handleDoctorBooking = async (doctor, actionLabel) => {
    const doctorId = doctor?.id || doctor?.doctorId || doctor?.medecinId || doctor?.raw?.id;
    const name = doctor?.name || doctor?.fullName || doctor?.raw?.fullName || "";
    const visibleText = actionLabel || (assistantLanguage === "en" ? "Book appointment" : "Prendre rendez-vous");

    if (!ctx?.authenticated) {
      setAuthModalOpen(true);
      return;
    }

    if (!doctorId) {
      handleQuickReply(`prendre rendez-vous avec ${name}`.trim(), visibleText);
      return;
    }

    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        text: visibleText,
        links: [],
        action: null,
        timestamp: new Date().toLocaleTimeString(timeLocale, {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);

    setGuidedBusy(true);
    try {
      await startGuidedFlow({
        sessionId,
        role: "patient",
        actionId: PATIENT_BOOK_APPOINTMENT,
        locale: assistantLanguage,
        context: buildContext(),
      });

      const reply = await continueGuidedFlow({
        sessionId,
        role: "patient",
        flowId: PATIENT_BOOK_APPOINTMENT,
        stepId: "choose_doctor",
        locale: assistantLanguage,
        payload: {
          doctorId: String(doctorId),
          doctor: { ...doctor, id: String(doctorId), registered: true },
        },
      });
      appendGuidedResponse(reply);
    } catch (err) {
      console.error("[AssistantBubble] Doctor booking start error:", err);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "Je n'ai pas pu ouvrir le calendrier de rendez-vous pour le moment.",
          links: [],
          action: null,
          timestamp: new Date().toLocaleTimeString(timeLocale, {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
    } finally {
      setGuidedBusy(false);
    }
  };

  const renderDoctorCards = (m) => {
    const doctors = Array.isArray(m?.data?.doctors) ? m.data.doctors : [];
    if (!doctors.length) return null;

    return (
      <div className="asst-doctor-card-list">
        {doctors.slice(0, 8).map((doctor, index) => {
          const name = doctor?.name || doctor?.fullName || doctor?.raw?.fullName || doctor?.raw?.name || "Médecin";
          const specialty = doctor?.specialty || doctor?.specialite || "Spécialité non précisée";
          const address = doctor?.address || doctor?.clinicAddress || doctor?.city || "";
          const canBook = doctor?.canBook === true || doctor?.can_book === true || doctor?.registered === true;
          const rawActionLabel =
            doctor?.cardButtonLabel ||
            doctor?.buttonLabel ||
            doctor?.primaryActionLabel ||
            (canBook
              ? (assistantLanguage === "en" ? "Book appointment" : "Prendre rendez-vous")
              : (assistantLanguage === "en" ? "Invite to MediLink" : "Inviter à MediLink"));
          const actionLabel = localizeAssistantLinkText(rawActionLabel, assistantLanguage, "", assistantLinkLabels);

          return (
            <div className="asst-doctor-card" key={`${doctor?.id || name}-${index}`}>
              <div className="asst-doctor-card-main">
                <div className="asst-doctor-card-head">
                  <div className="asst-doctor-card-title">{name}</div>
                </div>

                <div className="asst-doctor-specialty">{specialty}</div>
                {address && <div className="asst-doctor-address">{address}</div>}
              </div>

              <button
                type="button"
                className={`asst-doctor-action ${canBook ? "is-booking" : "is-invite"}`}
                onClick={() => canBook ? handleDoctorBooking(doctor, actionLabel) : handleDoctorInvite(name, actionLabel)}
              >
                {actionLabel}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMedicalPlaces = (m) => {
    const places = Array.isArray(m?.data?.places) ? m.data.places : [];
    if (!places.length) return null;

    return (
      <div className="asst-place-card-list">
        {places.slice(0, 8).map((place, index) => {
          const name = place?.name || `Place #${index + 1}`;
          const category = place?.category_label || place?.category || m?.data?.category_label || "";
          const address = place?.address || "";
          const distance = place?.distance ?? place?.distance_km;
          const distanceLabel = Number.isFinite(Number(distance))
            ? Number(distance).toFixed(2).replace(/\.00$/, "")
            : "";
          const actions = Array.isArray(place?.actions) ? place.actions : [];
          const mapsAction = actions.find((action) => action?.type === "google_maps" || action?.external);
          const navAction = actions.find((action) => action?.type === "navigate" || action?.external === false);
          const mapsUrl = mapsAction?.url || place?.google_maps_url || place?.maps_url;
          const routeUrl = navAction?.url || place?.route_url;
          const mapsLabel = localizeAssistantLinkText(mapsAction?.label || "Google Maps", assistantLanguage, assistantCopy.googleMaps, assistantLinkLabels);
          const routeLabel = localizeAssistantLinkText(navAction?.label || assistantCopy.openPlacePage, assistantLanguage, assistantCopy.openPlacePage, assistantLinkLabels);

          return (
            <div className="asst-place-card" key={`${place?.id || name}-${index}`}>
              <div className="asst-place-card-main">
                <div className="asst-place-card-head">
                  <div className="asst-place-card-title">{name}</div>
                  {distanceLabel && (
                    <span className="asst-place-distance">{distanceLabel} km</span>
                  )}
                </div>
                {category && <div className="asst-place-category">{category}</div>}
                {address && <div className="asst-place-address">{address}</div>}
              </div>

              <div className="asst-place-actions">
                {mapsUrl && (
                  <button
                    type="button"
                    className="asst-place-action is-map"
                    onClick={() => handleExternalLinkClick(mapsUrl)}
                  >
                    {mapsLabel}
                  </button>
                )}
                {routeUrl && (
                  <button
                    type="button"
                    className="asst-place-action"
                    onClick={() => handleLinkClick(routeUrl, false)}
                  >
                    {routeLabel}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAssistantLoader = (variant = "chat") => (
    <div
      className={`${variant === "gestion" ? "asst-gestion-flow-loading" : "asst-typing"} asst-reload-indicator`}
      role="status"
      aria-live="polite"
      aria-label={t("medaz.chat.typingAriaLabel")}
    >
      <span className="asst-reload-spinner" aria-hidden="true" />
      <span className="asst-dots" aria-hidden="true">
        <span></span><span></span><span></span>
      </span>
    </div>
  );

  const renderAppointmentSlots = (m) => {
    const slots = Array.isArray(m?.data?.appointment_slots) ? m.data.appointment_slots : [];
    if (!slots.length) return null;

    return (
      <div className="asst-slot-list">
        {slots.slice(0, 8).map((slot, index) => {
          const label = slot?.label || `${slot?.startTime || ""} - ${slot?.endTime || ""}`.trim();
          const command = slot?.command || `confirmer rendez-vous créneau #${slot?.id || index + 1}`;
          const actionLabel = localizeAssistantLinkText(slot?.actionLabel, assistantLanguage, assistantCopy.chooseSlot, assistantLinkLabels);
          const slotNumber = slot?.index || slot?.id || index + 1;
          const isRescheduleSlot =
            slot?.flow === "patient_reschedule" ||
            m?.data?.pending_patient_reschedule ||
            m?.data?.pending_reschedule ||
            m?.data?.appointment_id;
          const quickCommand = slot?.command || (
            isRescheduleSlot
              ? (assistantLanguage === "en" ? `choose reschedule slot #${slotNumber}` : `choisir creneau #${slotNumber} pour modifier rendez-vous`)
              : command
          );

          return (
            <div className="asst-slot-card appointment" key={`${slot?.id || index}-${label}`}>
              <div className="asst-slot-main">
                <span className="asst-slot-id">{assistantCopy.slot}</span>
                <span className="asst-slot-title">{slot?.doctorName || m?.data?.doctor?.name || assistantCopy.doctor}</span>
              </div>
              <div className="asst-slot-time">{label}</div>
              <div className="asst-slot-footer">
                <span className="asst-slot-status is-ok">{assistantCopy.available}</span>
                <div className="asst-slot-actions">
                  <button
                    type="button"
                    className="asst-slot-action-primary"
                    onClick={() => handleQuickReply(quickCommand, actionLabel)}
                  >
                    {actionLabel}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAvailableDays = (m) => {
    const days = Array.isArray(m?.data?.available_days) ? m.data.available_days : [];
    if (!days.length) return null;

    return (
      <div className="asst-slot-list">
        {days.slice(0, 12).map((day, index) => {
          const label = day?.label || day?.date || `${assistantCopy.viewDaySlots} #${index + 1}`;
          const command = day?.command || (
            assistantLanguage === "en"
              ? `view reschedule slots for ${day?.date || ""}`
              : `voir les créneaux du ${day?.date || ""} pour modifier rendez-vous`
          );
          const actionLabel = localizeAssistantLinkText(
            day?.actionLabel || assistantCopy.viewDaySlots,
            assistantLanguage,
            assistantCopy.viewDaySlots,
            assistantLinkLabels
          );
          const count = Number(day?.slots_count || day?.count || 0);

          return (
            <div className="asst-slot-card availability" key={`${day?.date || index}-${count}`}>
              <div className="asst-slot-main">
                <span className="asst-slot-id">{assistantCopy.viewAvailableSlots}</span>
                <span className="asst-slot-title">{day?.date || label}</span>
              </div>
              {count > 0 && (
                <div className="asst-slot-time">
                  {count} {assistantCopy.slotsAvailable}
                </div>
              )}
              <div className="asst-slot-footer">
                <span className="asst-slot-status is-ok">{assistantCopy.available}</span>
                <div className="asst-slot-actions">
                  <button
                    type="button"
                    className="asst-slot-action-primary"
                    onClick={() => handleQuickReply(command, actionLabel)}
                  >
                    {actionLabel}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAvailablePeriods = (m) => {
    const periods = Array.isArray(m?.data?.available_periods) ? m.data.available_periods : [];
    if (!periods.length) return null;

    return (
      <div className="asst-slot-list">
        {periods.slice(0, 8).map((period, index) => {
          const periodId = period?.period_id || period?.id || index + 1;
          const title = period?.title || period?.label || `${period?.start_date || ""} → ${period?.end_date || ""}`;
          const command = period?.command || (
            assistantLanguage === "en"
              ? `view reschedule slots for period ${periodId}`
              : `voir les creneaux pour la periode ${periodId}`
          );
          const actionLabel = localizeAssistantLinkText(
            period?.actionLabel || period?.label || assistantCopy.viewDaySlots,
            assistantLanguage,
            assistantCopy.viewDaySlots,
            assistantLinkLabels
          );
          const count = Number(period?.available_slots_count || period?.slots_count || 0);

          return (
            <div className="asst-slot-card availability" key={`${periodId}-${index}`}>
              <div className="asst-slot-main">
                <span className="asst-slot-id">{assistantCopy.period}</span>
                <span className="asst-slot-title">{title}</span>
              </div>
              {(period?.start_date || period?.end_date) && (
                <div className="asst-slot-time">
                  {period?.start_date || ""}{period?.end_date ? ` → ${period.end_date}` : ""}
                </div>
              )}
              {count > 0 && (
                <div className="asst-slot-time">
                  {count} {assistantCopy.slotsAvailable}
                </div>
              )}
              <div className="asst-slot-footer">
                <span className="asst-slot-status is-ok">{assistantCopy.available}</span>
                <div className="asst-slot-actions">
                  <button
                    type="button"
                    className="asst-slot-action-primary"
                    onClick={() => handleQuickReply(command, actionLabel)}
                  >
                    {actionLabel}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const expandStructuredMessage = (messageIndex) => {
    setExpandedMessageIndexes((prev) => {
      const next = new Set(prev);
      next.add(messageIndex);
      return next;
    });
  };

  const renderMessageText = (m, messageIndex) => {
    const structured = parseStructuredMessage(m.text);

    if (!structured.items.length) {
      return <div className="asst-message-text">{m.text}</div>;
    }

    const limit = Number(m?.data?.visible_limit || 8);
    const isExpanded = expandedMessageIndexes.has(messageIndex);
    const visibleItems = isExpanded ? structured.items : structured.items.slice(0, limit);
    const hiddenCount = Math.max(0, structured.items.length - visibleItems.length);

    return (
      <div className="asst-message-rich">
        {structured.narrative && (
          <div className="asst-message-text">{structured.narrative}</div>
        )}

        <div className="asst-slot-list">
          {visibleItems.map((item) => (
            <div className={`asst-slot-card ${item.kind}`} key={`${item.kind}-${item.id}-${item.raw}`}>
              {item.kind !== "availability" && (
                <div className="asst-slot-main">
                  <span className="asst-slot-id">
                    {item.kind === "appointment"
                      ? assistantCopy.appointment
                      : item.kind === "event"
                        ? assistantCopy.event
                      : item.kind === "eventSuggestion"
                        ? assistantCopy.window
                        : assistantCopy.slot}
                  </span>
                  {item.title && <span className="asst-slot-title">{item.title}</span>}
                </div>
              )}

              <div className="asst-slot-time">{item.datePart}</div>

              <div className="asst-slot-footer">
                {item.status && (
                  <span className={`asst-slot-status ${statusClassName(item.status)}`}>
                    {translateAssistantStatus(item.status, assistantLanguage)}
                  </span>
                )}

                {m.sender === "bot" && item.kind === "appointment" && /scheduled|pending/i.test(item.status) && (
                  <div className="asst-slot-actions">
                    {mode === "patient" ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleAppointmentManagementClick(item.id, PATIENT_RESCHEDULE_APPOINTMENT, assistantCopy.editAppointment)}
                        >
                          {assistantCopy.editAppointment}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAppointmentManagementClick(item.id, PATIENT_CANCEL_APPOINTMENT, assistantCopy.cancelAppointment)}
                        >
                          {assistantCopy.cancelAppointment}
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => handleQuickReply(`accepter #${item.id}`, assistantCopy.acceptAppointment)}>
                          {assistantCopy.acceptAppointment}
                        </button>
                        <button type="button" onClick={() => handleQuickReply(`refuser #${item.id}`, assistantCopy.rejectAppointment)}>
                          {assistantCopy.rejectAppointment}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {m.sender === "bot" && item.kind === "availability" && (
                  <div className="asst-slot-actions">
                    <button
                      type="button"
                      className="asst-slot-action-primary"
                      onClick={() => {
                        const targetDate = extractStructuredItemDate(item.datePart);
                        const command = assistantLanguage === "en"
                          ? `show available slots for ${targetDate}`
                          : `voir les slots disponibles le ${targetDate}`;
                        handleQuickReply(command, assistantCopy.viewAvailableSlots);
                      }}
                    >
                      {assistantCopy.viewAvailableSlots}
                    </button>
                  </div>
                )}

                {m.sender === "bot" && item.kind === "eventSuggestion" && (
                  <div className="asst-slot-actions">
                    <button
                      type="button"
                      className="asst-slot-action-primary"
                      onClick={() => handleQuickReply(
                        `ok ajouter dans cette plage - ${item.datePart}`,
                        `${assistantCopy.addInWindow} : ${item.datePart}`,
                      )}
                    >
                      {assistantCopy.addInWindow}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {hiddenCount > 0 && (
            <div className="asst-slot-actions asst-show-more-actions">
              <button
                type="button"
                className="asst-slot-action-primary"
                onClick={() => expandStructuredMessage(messageIndex)}
              >
                {assistantCopy.showMore} ({hiddenCount})
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ─── actions ─── */
  const handleUserGuide = () => {
    setChatMode("assistant");
    setScreen(SCREEN_CHAT);
    if (messages.length === 0) {
      setMessages([buildWelcomeMessage()]);
    }
  };

  const handleMedicalAssistant = () => {
    const MEDICAL_ASSISTANT = "/medicalassistant";
    if (!ctx?.authenticated) {
      setAuthModalOpen(true);
      return;
    }
    setOpen(false);
    setTimeout(() => { window.location.href = MEDICAL_ASSISTANT; }, 300);
  };

  const openGestionSection = () => {
    if (!ctx?.authenticated) {
      setAuthModalOpen(true);
      return;
    }
    setChatMode("gestion");
    setInput("");
    setMessages([]);
    setScreen(SCREEN_GESTION);
  };

  const openAssistantSection = async () => {
    if (!ctx?.authenticated) {
      setAuthModalOpen(true);
      return;
    }
    if (guidedBusy) return;

    const actionId = resolvedMode === "doctor" ? "doctor_diagnostic" : "patient_health_qa";
    setChatMode("assistant-ia");
    setScreen(SCREEN_CHAT);
    setGuidedBusy(true);

    try {
      const reply = await startGuidedFlow({
        sessionId,
        role: resolvedMode,
        actionId,
        locale: assistantLanguage,
        context: buildContext(),
      });
      setMessages([buildGuidedBotMessage(reply)]);
    } catch (err) {
      console.error("[AssistantBubble] Assistant IA start error:", err);
      setMessages([
        {
          sender: "bot",
          text: "Je n'ai pas pu demarrer l'assistant IA pour le moment.",
          links: [],
          action: null,
          timestamp: new Date().toLocaleTimeString(timeLocale, {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
    } finally {
      setGuidedBusy(false);
    }
  };

  const openAgentSection = () => {
    if (!ctx?.authenticated) {
      setAuthModalOpen(true);
      return;
    }
    setScreen(SCREEN_AGENT);
  };

  const executeLandingSection = (sectionId) => {
    if (sectionId === "gestion") {
      openGestionSection();
      return;
    }
    if (sectionId === "agent") {
      openAgentSection();
      return;
    }
    openAssistantSection();
  };

  const handleLandingSection = (sectionId) => {
    try {
      if (localStorage.getItem(`ml_onboarding_${sectionId}_seen`) === "true") {
        executeLandingSection(sectionId);
        return;
      }
    } catch {
      executeLandingSection(sectionId);
      return;
    }
    setPendingSection(sectionId);
    setOnboardingSection(sectionId);
  };

  const handleOnboardingClose = (sectionId) => {
    const targetSection = sectionId || pendingSection || onboardingSection || "assistant";
    setOnboardingSection(null);
    setPendingSection(null);
    executeLandingSection(targetSection);
  };

  const handleOpenPanel = () => {
    setOpen(true);
    setScreen(SCREEN_LANDING);
    fetchUserLocation();
  };

  const handleBackToLanding = () => setScreen(SCREEN_LANDING);

  /* ===================== FAB DRAG & DROP ===================== */
  const handleFabMouseDown = (e) => {
    if (!fabRef.current) return;
    setIsDraggingFab(true);
    setFabHasMoved(false);
    const rect = fabRef.current.getBoundingClientRect();
    setFabDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    e.preventDefault();
  };

  const handleFabMouseMove = (e) => {
    if (!isDraggingFab) return;
    setFabHasMoved(true);
    const vw = window.innerWidth, vh = window.innerHeight, fabSize = 100;
    let newRight = Math.max(0, Math.min(vw - e.clientX - (fabSize - fabDragOffset.x), vw - fabSize));
    let newBottom = Math.max(0, Math.min(vh - e.clientY - (fabSize - fabDragOffset.y), vh - fabSize));
    setFabPosition({ right: newRight, bottom: newBottom });
  };

  const handleFabMouseUp = () => setIsDraggingFab(false);

  const handleFabTouchStart = (e) => {
    if (!fabRef.current) return;
    setIsDraggingFab(true);
    setFabHasMoved(false);
    const touch = e.touches[0];
    const rect = fabRef.current.getBoundingClientRect();
    setFabDragOffset({ x: touch.clientX - rect.left, y: touch.clientY - rect.top });
    e.preventDefault();
  };

  const handleFabTouchMove = (e) => {
    if (!isDraggingFab) return;
    setFabHasMoved(true);
    const touch = e.touches[0];
    const vw = window.innerWidth, vh = window.innerHeight, fabSize = 100;
    let newRight = Math.max(0, Math.min(vw - touch.clientX - (fabSize - fabDragOffset.x), vw - fabSize));
    let newBottom = Math.max(0, Math.min(vh - touch.clientY - (fabSize - fabDragOffset.y), vh - fabSize));
    setFabPosition({ right: newRight, bottom: newBottom });
  };

  const handleFabTouchEnd = () => setIsDraggingFab(false);

  useEffect(() => {
    if (isDraggingFab) {
      document.addEventListener("mousemove", handleFabMouseMove);
      document.addEventListener("mouseup", handleFabMouseUp);
      document.addEventListener("touchmove", handleFabTouchMove);
      document.addEventListener("touchend", handleFabTouchEnd);
      return () => {
        document.removeEventListener("mousemove", handleFabMouseMove);
        document.removeEventListener("mouseup", handleFabMouseUp);
        document.removeEventListener("touchmove", handleFabTouchMove);
        document.removeEventListener("touchend", handleFabTouchEnd);
      };
    }
  }, [isDraggingFab, fabDragOffset]);

  /* ===================== PANEL DRAG & DROP ===================== */
  const handlePanelMouseDown = (e) => {
    if (!panelRef.current) return;
    const isInteractive = e.target.closest("button, input, textarea, a, .asst-category-btn, .asst-bubble, .asst-message-text, .ml-onboarding, .agent-ia-page");
    if (isInteractive) return;
    setIsDraggingPanel(true);
    const rect = panelRef.current.getBoundingClientRect();
    setPanelDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    e.preventDefault();
  };

  const handlePanelMouseMove = (e) => {
    if (!isDraggingPanel || !panelRef.current) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    const { width: pw, height: ph } = panelRef.current.getBoundingClientRect();
    let newRight = Math.max(-(pw - 100), Math.min(vw - e.clientX - (pw - panelDragOffset.x), vw - 100));
    let newBottom = Math.max(-(ph - 68), Math.min(vh - e.clientY - (ph - panelDragOffset.y), vh - 68));
    setPanelPosition({ right: newRight, bottom: newBottom });
  };

  const handlePanelMouseUp = () => setIsDraggingPanel(false);

  const handlePanelTouchStart = (e) => {
    if (!panelRef.current) return;
    const isInteractive = e.target.closest("button, input, textarea, a, .asst-category-btn, .asst-bubble, .asst-message-text, .ml-onboarding, .agent-ia-page");
    if (isInteractive) return;
    setIsDraggingPanel(true);
    const touch = e.touches[0];
    const rect = panelRef.current.getBoundingClientRect();
    setPanelDragOffset({ x: touch.clientX - rect.left, y: touch.clientY - rect.top });
    e.preventDefault();
  };

  const handlePanelTouchMove = (e) => {
    if (!isDraggingPanel || !panelRef.current) return;
    const touch = e.touches[0];
    const vw = window.innerWidth, vh = window.innerHeight;
    const { width: pw, height: ph } = panelRef.current.getBoundingClientRect();
    let newRight = Math.max(-(pw - 100), Math.min(vw - touch.clientX - (pw - panelDragOffset.x), vw - 100));
    let newBottom = Math.max(-(ph - 68), Math.min(vh - touch.clientY - (ph - panelDragOffset.y), vh - 68));
    setPanelPosition({ right: newRight, bottom: newBottom });
  };

  const handlePanelTouchEnd = () => setIsDraggingPanel(false);

  useEffect(() => {
    if (isDraggingPanel) {
      document.addEventListener("mousemove", handlePanelMouseMove);
      document.addEventListener("mouseup", handlePanelMouseUp);
      document.addEventListener("touchmove", handlePanelTouchMove);
      document.addEventListener("touchend", handlePanelTouchEnd);
      return () => {
        document.removeEventListener("mousemove", handlePanelMouseMove);
        document.removeEventListener("mouseup", handlePanelMouseUp);
        document.removeEventListener("touchmove", handlePanelTouchMove);
        document.removeEventListener("touchend", handlePanelTouchEnd);
      };
    }
  }, [isDraggingPanel, panelDragOffset]);

  /* ===================== RENDER ===================== */
  return (
    <>
      {/* ✅ Auth Required Popup */}
      <AuthRequiredModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
      {onboardingSection && (
        <OnboardingModal
          sectionId={onboardingSection}
          role={resolvedMode}
          locale={assistantLanguage}
          onClose={handleOnboardingClose}
        />
      )}

      {/* ─── FAB DRAGGABLE ─── */}
      {!open && (
        <button
          ref={fabRef}
          className={`asst-fab ${isDraggingFab ? "asst-fab--dragging" : ""}`}
          style={{
            right: `${fabPosition.right}px`,
            bottom: `${fabPosition.bottom}px`,
            cursor: isDraggingFab ? "grabbing" : "grab",
          }}
          onMouseDown={handleFabMouseDown}
          onTouchStart={handleFabTouchStart}
          onClick={() => { if (!fabHasMoved) handleOpenPanel(); }}
          aria-label={t("medaz.fab.ariaLabel")}
          title={t("medaz.fab.title")}
        >
          <RobotIcon size={46} className="asst-fab-icon" />
        </button>
      )}

      {/* ─── Panel DRAGGABLE ─── */}
      {open && (
        <div
          ref={panelRef}
          className={`asst-panel ${isMax ? "asst-panel--max" : ""} ${screen === SCREEN_CHAT && chatMode === "assistant-ia" ? "asst-panel--assistant-page" : ""} ${screen === SCREEN_AGENT ? "asst-panel--agent-page" : ""} ${isDraggingPanel ? "asst-panel--dragging" : ""}`}
          style={{ right: `${panelPosition.right}px`, bottom: `${panelPosition.bottom}px` }}
          role="dialog"
          dir={isRtl ? "rtl" : "ltr"}
          data-assistant-locale={assistantLanguage}
          aria-label={t("medaz.panel.ariaLabel")}
          onMouseDown={handlePanelMouseDown}
          onTouchStart={handlePanelTouchStart}
        >
          {/* ── header ── */}
          <div className="asst-head">
            <div className="asst-title">
              {screen !== SCREEN_LANDING && (
                <button
                  className="asst-back-btn"
                  onClick={handleBackToLanding}
                  aria-label={t("medaz.header.back")}
                  title={t("medaz.header.back")}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 3.75L6.75 9L11.25 14.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}

              <div className="asst-icon-wrapper">
                <span className="asst-icon"><RobotIcon size={26} /></span>
                <span className="asst-icon-status"></span>
              </div>

              <div className="asst-title-text">
                <span className="asst-title-main">{assistantPanelCopy.titleMain}</span>
              </div>
            </div>

            <div className="asst-head-actions">
              {screen === SCREEN_AGENT && (
                <button
                  type="button"
                  className="asst-new-conversation"
                  onClick={handleNewConversation}
                  aria-label={t("assistantIA.reset", { ns: "assistantAi", lng: assistantLanguage })}
                  title={t("assistantIA.reset", { ns: "assistantAi", lng: assistantLanguage })}
                >
                  <MessageSquarePlus size={17} aria-hidden="true" />
                </button>
              )}
              <div className="asst-language-toggle" role="group" aria-label={assistantPanelCopy.languageLabel}>
                {["fr", "en", "ar"].map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    className={`asst-language-btn ${assistantLanguage === lang ? "is-active" : ""}`}
                    onClick={() => handleAssistantLanguageChange(lang)}
                    aria-pressed={assistantLanguage === lang}
                    title={lang === "fr" ? assistantPanelCopy.replyFr : lang === "ar" ? assistantPanelCopy.replyAr : assistantPanelCopy.replyEn}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>

              <button
                className="asst-expand"
                onClick={() => setIsMax((v) => !v)}
                aria-label={isMax ? t("medaz.header.reduce") : t("medaz.header.expand")}
                title={isMax ? t("medaz.header.reduce") : t("medaz.header.expand")}
              >
                {!isMax && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 16 14" fill="none">
                    <path d="M13.8472 1.29167L9.18056 5.29167M6.06944 7.95833L1.40278 11.9583M14.625 4.161V0.625H10.4997M0.625 9.089V12.625H4.75033" stroke="currentColor" strokeWidth="1.25" />
                  </svg>
                )}
                {isMax && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 16 14" fill="none">
                    <path d="M14.625 0.625L10.5 4.75M10.5 0.625V4.75H14.625" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M0.625 12.625L4.75 8.5M4.75 12.625V8.5H0.625" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>

              <button
                className="asst-x"
                onClick={() => { setOpen(false); setIsMax(false); }}
                aria-label={t("medaz.header.close")}
                title={t("medaz.header.closeTitle")}
              >
                ×
              </button>
            </div>
          </div>

          {/* ══════════ LANDING SCREEN ══════════ */}
          {screen === SCREEN_LANDING && (
            <div className="asst-landing">
              <div className="asst-landing-illustration">
                <img src={RobotGauche} alt="" aria-hidden="true" className="asst-side-robot asst-side-robot--left" />
                <img src={RobotDroite} alt="" aria-hidden="true" className="asst-side-robot asst-side-robot--right" />
                <img src={Robot} alt={t("medaz.landing.robotAlt")} className="asst-landing-robot" />
              </div>

              <div className="asst-landing-greeting">
                <h2 className="asst-landing-hey">{t("medaz.landing.greeting")}</h2>
                <p className="asst-landing-sub">{t("medaz.landing.subtitle")}</p>
              </div>

              <div className="asst-landing-categories">
                <button className="asst-category-btn asst-category-btn--gestion" onClick={() => handleLandingSection("gestion")}>
                  <LandingSectionLogo type="gestion" />
                  <span className="asst-category-icon-wrap asst-category-icon-guide">
                    <span aria-hidden="true">📘</span>
                  </span>
                  <div className="asst-category-text">
                    <span className="asst-category-title">{landingSections[0].title}</span>
                    <span className="asst-category-kicker">{landingSections[0].subtitle}</span>
                    <span className="asst-category-desc">{landingSections[0].description}</span>
                  </div>
                </button>

                <button className="asst-category-btn asst-category-btn--assistant" onClick={() => handleLandingSection("assistant")}>
                  <LandingSectionLogo type="assistant" />
                  <span className="asst-category-icon-wrap asst-category-icon-medical">
                    <span aria-hidden="true">🩺</span>
                  </span>
                  <div className="asst-category-text">
                    <span className="asst-category-title">{landingSections[1].title}</span>
                    <span className="asst-category-kicker">{landingSections[1].subtitle}</span>
                    <span className="asst-category-desc">{landingSections[1].description}</span>
                  </div>
                </button>
                <button className="asst-category-btn asst-category-btn--agent" onClick={() => handleLandingSection("agent")}>
                  <LandingSectionLogo type="agent" />
                  <span className="asst-category-icon-wrap" aria-hidden="true">
                    <span>A</span>
                  </span>
                  <div className="asst-category-text">
                    <span className="asst-category-title">{landingSections[2].title}</span>
                    <span className="asst-category-kicker">{landingSections[2].subtitle}</span>
                    <span className="asst-category-desc">{landingSections[2].description}</span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* ══════════ CHAT SCREEN ══════════ */}
          {screen === SCREEN_GESTION && (
            <div className={`asst-gestion-screen ${gestionHasActiveFlow ? "asst-gestion-screen--with-flow" : ""}`}>
              <div className="asst-chat-watermark asst-chat-watermark--gestion">
                <img src={RobotGauche} alt="" aria-hidden="true" className="asst-chat-watermark-img" />
              </div>

              {!gestionHasActiveFlow && (
                <div className="asst-gestion-fixed-zone">
                  <div className="asst-gestion-actions">
                    {renderQuickActions(gestionQuickActions, "gestion-quick-actions", "guided-quick-actions--gestion")}
                  </div>
                </div>
              )}

              {gestionHasActiveFlow && (
                <div className="asst-gestion-flow-panel">
                  <div className="asst-gestion-flow-toolbar">
                    <div className="asst-gestion-flow-heading">
                      <span>{assistantPanelCopy.flowKicker}</span>
                      <strong>{assistantPanelCopy.flowTitle}</strong>
                    </div>
                    <button
                      type="button"
                      className="asst-gestion-home-btn"
                      onClick={handleGestionHomeReturn}
                    >
                      {assistantPanelCopy.flowHome}
                    </button>
                  </div>

                  <div className="asst-gestion-flow-content">
                    {messages
                      .filter((message) => message?.sender === "bot" && message?.ui?.type !== UI_QUICK_ACTIONS && (message?.ui || message?.text))
                      .map((message, index) => (
                        <div className="asst-gestion-flow-item" key={`gestion-flow-${index}`}>
                          {message.ui ? (
                            <GuidedFlowRenderer
                              ui={message.ui}
                              onContinue={handleGuidedContinue}
                              onCancel={handleGuidedCancel}
                              onShowMenu={handleGestionHomeReturn}
                            />
                          ) : (
                            <div className="asst-gestion-flow-text" role="status">{message.text}</div>
                          )}
                        </div>
                      ))}

                    {guidedBusy && renderAssistantLoader("gestion")}
                  </div>
                </div>
              )}
            </div>
          )}

          {screen === SCREEN_AGENT && (
            <AgentIA
              key={agentSessionId}
              role={resolvedMode}
              locale={assistantLanguage}
              onLocaleChange={handleAssistantLanguageChange}
              onBack={handleBackToLanding}
              onOpenAssistantIA={openAssistantSection}
              context={buildContext(agentSessionId)}
              guidedSessionKey={sessionId}
              showSectionHeader={false}
            />
          )}

          {screen === SCREEN_CHAT && chatMode === "assistant-ia" && (
            <AssistantIA
              messages={messages}
              loading={guidedBusy}
              locale={assistantLanguage}
              onContinue={handleGuidedContinue}
              onCancel={handleGuidedCancel}
              onShowMenu={appendQuickActionsMenu}
            />
          )}

          {screen === SCREEN_CHAT && chatMode !== "assistant-ia" && (
            <>
              <div
                className="asst-body asst-body-chat"
                id="asst-scroll"
                ref={scrollRef}
              >
                <div className="asst-chat-watermark">
                  <img src={RobotGauche} alt="" aria-hidden="true" className="asst-chat-watermark-img" />
                </div>

                {messages.map((m, i) => {
                  const shouldRenderText = !m.ui || m.ui.type === "text";

                  return (
                  <div
                    key={i}
                    className={`asst-msg ${m.sender === "user" ? "me" : "bot"} ${m.ui ? "asst-msg--guided" : ""}`}
                    style={{ position: "relative", zIndex: 1 }}
                  >
                    {m.sender === "bot" && (
                      <div className="asst-avatar-small"><RobotIcon size={22} /></div>
                    )}

                    <div className="asst-bubble">
                      {shouldRenderText && renderMessageText(m, i)}
                      {m.ui?.type === UI_QUICK_ACTIONS && renderQuickActions(m.ui?.props?.groups, `msg-quick-actions-${i}`)}
                      {m.ui && m.ui.type !== UI_QUICK_ACTIONS && (
                        <GuidedFlowRenderer
                          ui={m.ui}
                          onContinue={handleGuidedContinue}
                          onCancel={handleGuidedCancel}
                          onShowMenu={appendQuickActionsMenu}
                        />
                      )}
                      {m.sender === "bot" && renderAppointmentSlots(m)}
                      {m.sender === "bot" && renderAvailableDays(m)}
                      {m.sender === "bot" && renderAvailablePeriods(m)}
                      {m.sender === "bot" && renderDoctorCards(m)}
                      {m.sender === "bot" && renderMedicalPlaces(m)}

                      {m.sender === "bot" && isMedicalAssistantRedirectMessage(m) && (
                        <div className="asst-actions asst-actions-primary">
                          <button
                            type="button"
                            className="asst-btn asst-btn-tour"
                            onClick={openAssistantSection}
                            title={getMedicalAssistantRedirectLabel(m)}
                          >
                            <span className="asst-btn-icon">IA</span>
                            <span className="asst-btn-text">{getMedicalAssistantRedirectLabel(m)}</span>
                          </button>
                        </div>
                      )}

                      {m.action?.type === "NAVIGATE" && m.action?.url && !m.links?.length && (
                        <div className="asst-actions asst-actions-primary">
                          <button
                            type="button"
                            className="asst-btn asst-btn-direct"
                            onClick={() => handleLinkClick(m.action.url, false)}
                            title={assistantCopy.open}
                          >
                            <span className="asst-btn-icon">↗</span>
                            <span className="asst-btn-text">{assistantCopy.open}</span>
                          </button>
                        </div>
                      )}

                      {m.links?.length > 0 && (
                        <div className="asst-actions">
                          {m.links.map((link, j) => (
                            <div key={`${i}-${j}`} className="asst-action-group">
                              <button
                                type="button"
                                className="asst-btn asst-btn-tour"
                                onClick={() => handleLinkClick(link.url, true)}
                                title={t("medaz.chat.linkButton.tourTitle")}
                              >
                                <span className="asst-btn-icon">{t("medaz.chat.linkButton.tourIcon")}</span>
                                <span className="asst-btn-text">
                                  {localizeAssistantLinkText(
                                    link.text,
                                    assistantLanguage,
                                    t("medaz.chat.linkButton.defaultLinkText"),
                                    assistantLinkLabels
                                  )}
                                </span>
                              </button>

                              <button
                                type="button"
                                className="asst-btn asst-btn-direct"
                                onClick={() => handleLinkClick(link.url, false)}
                                title={assistantCopy.direct}
                              >
                                <span className="asst-btn-icon">{t("medaz.chat.linkButton.directIcon")}</span>
                                <span className="asst-btn-text">
                                  {localizeAssistantLinkText(assistantCopy.direct, assistantLanguage, assistantCopy.direct, assistantLinkLabels)}
                                </span>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {m.meta && process.env.NODE_ENV === "development" && (
                        <div className="asst-meta">
                          <small>
                            {t("medaz.chat.meta.intent")}: {m.meta.intent} |{" "}
                            {t("medaz.chat.meta.confidence")}:{" "}
                            {typeof m.meta.confidence === "number"
                              ? m.meta.confidence.toFixed(2)
                              : m.meta.confidence}{" "}
                            | {t("medaz.chat.meta.router")}: {m.meta.router}
                          </small>
                        </div>
                      )}

                      {m.timestamp && <div className="asst-timestamp">{m.timestamp}</div>}
                    </div>
                  </div>
                  );
                })}

                {chatMode === "gestion" && userMessageCount === 0 && (quickActionsLoading || quickActionsError || gestionQuickActions.length > 0) && (
                  renderQuickActions(gestionQuickActions, "welcome-quick-actions")
                )}

                {busy && (
                  <div className="asst-msg bot" style={{ position: "relative", zIndex: 1 }}>
                    <div className="asst-avatar-small"><RobotIcon size={22} /></div>
                    {renderAssistantLoader("chat")}
                  </div>
                )}
              </div>

              {chatMode === "assistant" && (
                <div className="asst-input-wrapper">
                  <div className="asst-input">
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onKeyDown}
                      placeholder={assistantCopy.placeholder}
                      rows={1}
                      disabled={busy}
                      aria-label={t("medaz.chat.messageAriaLabel")}
                    />
                    <button
                      className="asst-send"
                      onClick={() => handleSend(input)}
                      disabled={busy || !input.trim()}
                      aria-label={t("medaz.chat.sendAriaLabel")}
                      title={t("medaz.chat.sendTitle")}
                    >
                      <span className="asst-send-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                          <path d="M11.0762 20.9379L16.8922 17.5287C21.973 14.5505 24.5134 13.0614 24.3904 10.944C24.2675 8.82659 21.5718 7.64155 16.1804 5.27149L10.0089 2.55845C5.65753 0.645574 3.48185 -0.310863 2.27611 0.0915316C1.12906 0.474335 0.272903 1.45685 0.0335171 2.66509C-0.218119 3.93517 0.963725 6.03211 3.32741 10.226C3.62035 10.7457 4.17641 11.0705 4.76212 11.0393L14.333 10.5291C14.8714 10.5004 15.3318 10.923 15.3612 11.473C15.3906 12.023 14.978 12.4921 14.4395 12.5208L5.02322 13.0227C4.37766 13.0571 3.80651 13.489 3.55212 14.0954C1.68982 18.5346 0.758649 20.7543 1.15561 21.9867C1.53325 23.1591 2.4974 24.0359 3.68104 24.2834C4.92526 24.5435 6.9756 23.3416 11.0762 20.9379Z" fill="#006eea"/>
                        </svg>
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
